import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useResponsive } from '../../../hooks/useResponsive';
import { useCancelRenewalFigo, useMixpanel } from '../../../hooks';
import {
  delay,
  doNothing,
  getNowISOString,
  possesiveAdaptationI18n,
} from '../../../services/utils';

import { cancelRenewalModalVisibleChanged } from '../../../actions';

import {
  POWER_UPS_RELATION,
  WELLNESS_MULTIMODAL_ID,
} from '../../changeCoverage/powerups/powerups.constans';

import FigoLoadingOverlay from '../../common/FigoLoadingOverlay';

import { PowerupCard }
  from '../../changeCoverage/changeCoveragePreview/PowerupCard';
import { ChangeCoverageDetails } from './ChangeCoverageDetails';
import { ModifierModalInfo } from '../modifiers/ModifierModalInfo';
import { CancelRenewalModal } from './changeCoverageOptions/CancelRenewalModal';
import { useCoverageModal } from './hooks/useCoverageModal';

import { EV55, EV56 } from '../../../services/mixpanel';

import './css/ChangeCoverageScreenPreview.css';
import { formatShortDateByLanguage } from '../../../services/language.utils';
import { MARKETING_CHANNEL_ID } from '../../../theme/theme.constants';
import { getCompanyName } from '../../../theme/theme.utils';

export const {
  VETERINARY_EXAM_FEES_PSM_ID,
  WELLNESS_PSM_ID,
} = POWER_UPS_RELATION;

const ChangeCoverageScreenPreview = ({
  hideCopayCard = false,
  ignoreRate = false,
  loading = false,
  marketingChannel = MARKETING_CHANNEL_ID.figo,
  onChangeCoverageClick = () => { },
  onKeepClick = () => { },
  policyNumber = '',
  preview = {
    AnnualBenefit: '',
    CoverageName: '',
    Deductible: '',
    HavePerIncidentCopay: '',
    LastDateToChange: '',
    NewPremiumEffectiveDate: '',
    PerIncidentCopayInformation: '',
    PetName: '',
    Premium: '$0',
    Reimbursement: '',
  },
  powerUps = [{
    detail: '',
    id: VETERINARY_EXAM_FEES_PSM_ID,
    included: false,
    title: '',
  }],
}) => {
  const { t } = useTranslation('changeCoverage');
  const dispatch = useDispatch();
  const { isTablet, isMobile } = useResponsive();
  const {
    handlePetProtectCopayInfoClick,
    handleVetFeesClick,
    handleWellnessClick,
    multiModalCategoryId,
    multimodalDataSelected,
    multiModalVisible,
    setMultiModalVisible,
    wellnessBenefits,
  } = useCoverageModal(ignoreRate);
  const showCancelRenewal = useCancelRenewalFigo();
  const { sendEvent } = useMixpanel();

  const petInfo = useMemo(() => ({ petName: preview?.PetName }), [preview]);

  const brandOfPolicy = getCompanyName(marketingChannel);

  const handlePowerupClick = (id) => () => {
    switch (id) {
      case VETERINARY_EXAM_FEES_PSM_ID:
        handleVetFeesClick(id);
        break;

      case WELLNESS_PSM_ID: {
        const wellnessId = wellnessBenefits[0].CoverageLimitId;

        handleWellnessClick(wellnessId || WELLNESS_MULTIMODAL_ID.BASIC);
        break;
      }

      default:
        doNothing();
    }
  };

  function formatDate(date) {
    return !date ? '-' : formatShortDateByLanguage(date);
  }

  function handleCloseMultiModal() {
    setMultiModalVisible(false);
  }

  function handleClickCancelRenewal() {
    dispatch(cancelRenewalModalVisibleChanged(true));
  }

  function onClickReturnInsurance() {
    const nowISOString = getNowISOString();

    sendEvent(EV55({
      date: nowISOString,
      policyBrand: brandOfPolicy,
      policyNumber,
    }));

    delay(500).then(() => onKeepClick());
  }

  function handleChangeCoverageClick() {
    const nowISOString = getNowISOString();

    sendEvent(EV56({
      date: nowISOString,
      policyBrand: brandOfPolicy,
      policyNumber,
    }));

    onChangeCoverageClick();
  }

  return (
    <div className="Flex Flex-column">
      <div className="Change-coverage-screen-preview-container">
        <h3>
          {`${isTablet || isMobile
            ? t('preview.mTitle', {
              count: possesiveAdaptationI18n(preview.PetName),
              name: preview.PetName,
            })
            : t('preview.title', {
              count: possesiveAdaptationI18n(preview.PetName),
              name: preview.PetName,
            })}`}
        </h3>

        <ChangeCoverageDetails
          annualBenefit={preview.AnnualBenefit}
          coverageName={preview.CoverageName}
          deductible={preview.Deductible}
          effectiveDate={formatDate(preview.NewPremiumEffectiveDate)}
          lastDate={formatDate(preview.LastDateToChange)}
          marketingChannelId={marketingChannel}
          petName={preview.PetName}
          price={preview.Premium?.replace('$', '')}
          reimbursement={preview.Reimbursement}
        />

        {hideCopayCard
          ? null : (
            <div className="Change-coverage-preview-pet-protect-container">
              <PowerupCard
                detail={preview.PerIncidentCopayInformation}
                handleLearnMoreClick={handlePetProtectCopayInfoClick}
                included={preview.HavePerIncidentCopay}
                title={t('preview.petProtectCopayTitle')}
              />
            </div>
          )}

        <h3>{t('powerups.Powerups')}</h3>

        <p className="Change-coverage-preview-powerup-description">
          {t('preview.powerupsText', {
            count: possesiveAdaptationI18n(preview.PetName),
            name: preview.PetName,
          })}
        </p>

        <div className="Change-coverage-preview-powerup-powerups-container">
          {powerUps.map((powerup) => (
            <PowerupCard
              key={powerup.title}
              detail={powerup.detail}
              disabled={powerup.disabled}
              handleLearnMoreClick={handlePowerupClick(powerup.id)}
              included={powerup.included}
              title={powerup.title}
            />
          ))}
        </div>

        <FigoLoadingOverlay visible={loading} />

        <CancelRenewalModal petInfo={petInfo} />

        {multimodalDataSelected && (
          <ModifierModalInfo
            initialMenuSelected={multiModalCategoryId}
            isVisible={multiModalVisible}
            isWhatsCovered
            multiModalData={multimodalDataSelected}
            onClose={handleCloseMultiModal}
            wellnessBenefits={wellnessBenefits}
          />
        )}
      </div>

      <div className="Change-coverage-preview-bottom-container">
        <div className="Change-coverage-preview-continue">
          <div className="Change-coverage-preview-continue-content">
            <h3>{t('preview.continueWithCoverage')}</h3>

            <p>
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="preview.policyForAutoRenewal"
                ns="changeCoverage"
                values={{
                  name: preview.PetName,
                  renewalDate: formatDate(preview.NewPremiumEffectiveDate),
                }}
              />
            </p>
          </div>

          <button
            className="Change-coverage-preview-return-to-insurance-button"
            data-marketing-channel={marketingChannel}
            onClick={onClickReturnInsurance}
            type="button"
          >
            {t('preview.returnToInsurance')}
          </button>
        </div>

        <h4>{t('preview.wantChanges')}</h4>

        <div className="Change-coverage-preview-continue">
          <p className="Change-coverage-preview-want-changes-text">
            <Trans
              components={{ bold: <strong /> }}
              i18nKey={showCancelRenewal
                ? 'preview.cancelDescription'
                : 'preview.cancelDescriptionShort'}
              ns="changeCoverage"
              values={{ beforeDate: formatDate(preview.LastDateToChange) }}
            />
          </p>

          <div className="Change-coverage-preview-cancel-buttons">
            {showCancelRenewal && (
              <button
                className="Change-coverage-preview-cancel-text-button"
                onClick={handleClickCancelRenewal}
                type="button"
              >
                {t('preview.cancelRenewal')}
              </button>
            )}

            <button
              className="Change-coverage-preview-cancel-button"
              onClick={handleChangeCoverageClick}
              type="button"
            >
              {t('preview.changeCoverage')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ChangeCoverageScreenPreview };
